import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import Signature from "assets/icons/signature.svg?react";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import Button from "components/Button";
import pluginService from "api/pluginService";
import { blobToBase64 } from "utils/blobToBase64";
import signatureService from "api/signatureService";
import { useContext, useEffect, useState } from "react";
import moment from "moment";
import Spinner from "components/Spinner";
import PackagesContext from "contexts/PackagesContext";
import { useTranslation } from "react-i18next";

const SignatureLabel: FC<Props> = ({ content }) => {
  const { t } = useTranslation();
  const queryParams = new URLSearchParams(location.search);
  const { packages } = useContext(PackagesContext);

  const PDFId = queryParams.get("pdf");

  const splittedPDFId = PDFId?.split(":");

  const packId = splittedPDFId ? splittedPDFId[0] : null;
  const fileUri = splittedPDFId ? splittedPDFId[2] : null;
  const fileKind = splittedPDFId ? splittedPDFId[3] : null;
  const pack = packages.find((x) => x.Id === packId);
  const ownerAttachment =
    pack != null ? pack.Attachments.find((a) => a.Uri === fileUri) : null;
  const { mutate: verifyByPlugin, isLoading: isVerifing } =
    pluginService.useVerifySignature();
  const { mutate: uploadSignature, isLoading: isUploading } =
    signatureService.useUploadSignature();
  const {
    data: signatureData,
    isLoading,
    error,
  } = signatureService.useGetSignature(
    {
      OwnerId: packId!,
      ownerKind: Number(fileKind)!,
      attachmentId: ownerAttachment?.Id,
    },
    packId !== null
  );

  const [sig, setSig] = useState<any>(null);
  const [showSig, setShowSig] = useState<any>(false);
  const [more, setShowMore] = useState<any>(false);

  useEffect(() => {
    if (!signatureData?.data?.Id || error) {
      setSig(null);
      return;
    }

    setSig(JSON.parse(signatureData.data.Json));
  }, [signatureData, error]);

  const verify = async () => {
    const b: string = (await blobToBase64(content as Blob)) as string;
    verifyByPlugin(
      {
        Content: b.split(",")[1],
        ContentType: "application/pdf",
        FileName: "dokument.pdf",
      },
      {
        onSuccess: (response) => {
          setSig(response.data);
          uploadSignature({
            Json: JSON.stringify(response.data),
            OwnerId: packId,
            OwnerKind: Number(fileKind),
            OwnerAttachmentId: ownerAttachment?.Id,
          });
        },
      }
    );
  };

  return (
    <div className={styles.descr}>
      <div onClick={() => setShowSig(!showSig)} className={styles.descrWrapper}>
        <div className={styles.ss}>
          <Signature />
          {isLoading && (
            <>
              <Spinner></Spinner> {t("common.loadingInProgress")}
            </>
          )}

          {sig && (
            <>
              {t("signature.signedElectronically")} {sig.Signatures[0].ChainItem[0].CommonName}{" "}
              {moment(sig.Signatures[0].DateTime)
                .format("DD.MM.YYYY HH:mm")
                .toString()}
            </>
          )}

          {!isLoading && !sig && (
            <>{t("signature.signedUnverified")}</>
          )}
        </div>
        <div>
          <FontAwesomeIcon
            className={`${styles.icon} ${showSig && styles.revert}`}
            icon={faCaretDown}
          />
        </div>
      </div>
      {showSig && (
        <div className={styles.list}>
          <div className={styles.listWrapper}>
            {(isVerifing || isUploading) && !sig && (
              <div className={styles.loading}>
                <Spinner></Spinner> {t("signature.verifying")}
              </div>
            )}
            {!isVerifing && !isUploading && !sig && (
              <div className={styles.no}>
                {t("signature.notVerifiedYet")}
                <Button
                  className={styles.button}
                  text={t("signature.verifySignature")}
                  onClick={verify}
                />
              </div>
            )}
            {sig && (
              <div className={styles.signature}>
                <div className={styles.box}>
                  <div className={styles.label}>{t("signature.signedBy")}</div>
                  <div className={styles.notLabel}>
                    {sig.Signatures[0].ChainItem[0].CommonName}
                  </div>
                  {sig.Signatures[0].TrustedProfile && (
                    <div className={styles.notLabel}>
                      {t("signature.onBehalfOf")}{" "}
                      {sig.Signatures[0].TrustedProfile?.FirstName ?? ""}{" "}
                      {sig.Signatures[0].TrustedProfile?.LastName ?? ""} (PESEL:{" "}
                      {sig.Signatures[0].TrustedProfile?.PESEL ?? ""})
                    </div>
                  )}
                </div>
                <div className={styles.box}>
                  <div className={styles.label}>{t("signature.signatureDate")}</div>
                  <div className={styles.notLabel}>
                    {moment(sig.Signatures[0].DateTime)
                      .format("DD.MM.YYYY HH:mm:ss")
                      .toString()}
                  </div>
                </div>

                <div className={styles.box}>
                  <div className={styles.label}>{t("signature.signatureType")}</div>
                  <div className={styles.notLabel}>
                    {sig.Signatures[0].SigningCertificate.IsQualified
                      ? t("signature.trustedProfile")
                      : t("signature.nonQualified")}
                  </div>
                </div>

                {more && (
                  <>
                    <div className={styles.box}>
                      <div className={styles.label}>{t("signature.signatureFormat")}</div>
                      <div className={styles.notLabel}>
                        {sig.Signatures[0].SignatureFormat}
                      </div>
                    </div>

                    <div className={styles.box}>
                      <div className={styles.label}>{t("signature.signatureAlgorithm")}</div>
                      <div className={styles.notLabel}>
                        {sig.Signatures[0].DigestAlgorithm}
                      </div>
                    </div>
                    <div className={styles.box}>
                      <div className={styles.label}>{t("signature.issuedBy")}</div>
                      <div className={styles.notLabel}>
                        {sig.Signatures[0].SigningCertificate.Issuer}
                      </div>
                    </div>

                    <div className={styles.box}>
                      <div className={styles.label}>{t("signature.certificateValidity")}</div>
                      <div className={styles.notLabel}>
                        {moment(
                          sig.Signatures[0].SigningCertificate.ValidFrom
                        ).format("DD.MM.YYYY HH:mm:ss")}{" "}
                        -{" "}
                        {moment(
                          sig.Signatures[0].SigningCertificate.ValidTo
                        ).format("DD.MM.YYYY HH:mm:ss")}
                      </div>
                    </div>

                    <div className={styles.box}>
                      <div className={styles.label}>
                        {t("signature.externalTimestamp")}
                      </div>
                      <div className={styles.notLabel}>{t("signature.notPresent")}</div>
                    </div>
                  </>
                )}

                {!more && (
                  <div
                    onClick={() => setShowMore(true)}
                    className={styles.more}
                  >
                    {t("signature.details")}{" "}
                    <FontAwesomeIcon
                      className={styles.expander}
                      icon={faCaretDown}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureLabel;
